import { cn } from "@/lib/utils";

const pulseBar = "animate-pulse rounded-md bg-muted";

/** Shared with loaded basket page — totals and line cards align on mobile. */
export const basketTotalsColumnClass =
	"order-1 mx-auto flex w-full max-w-lg flex-col gap-6 py-12 xl:order-2 xl:mx-0 xl:max-w-none xl:pt-2 xl:pb-20";

export const basketItemsColumnClass =
	"order-2 mx-auto w-full max-w-lg xl:order-1 xl:mx-0 xl:max-w-none";

export const basketItemsGridClass =
	"grid w-full grid-cols-1 justify-items-stretch gap-8 md:grid-cols-[repeat(auto-fill,21rem)] md:justify-items-center md:gap-6 lg:grid-cols-[repeat(auto-fill,22rem)] lg:gap-12 xl:justify-items-stretch xl:gap-8 2xl:grid-cols-[repeat(auto-fill,25rem)]";

/** Mirrors [BasketGridItem]: 16:9 media, category, 3-line title, package row, express toggle. */
function BasketLineCardSkeleton() {
	return (
		<div className="relative flex h-auto min-h-0 w-full flex-col overflow-hidden rounded-lg border-none bg-card p-0 shadow-custom">
			<div className={cn(pulseBar, "aspect-video w-full shrink-0")} />
			<div className="flex min-h-0 flex-col px-3 pt-4 pb-2 sm:px-4 sm:pb-3">
				<div className="flex min-h-[1.25rem] items-center gap-2">
					<div className={cn(pulseBar, "h-3 w-24 shrink-0")} />
					<div className="min-w-0 flex-1" aria-hidden />
				</div>
				<div className="min-h-[3lh] w-full text-left">
					<div className={cn(pulseBar, "h-3 w-full")} />
					<div className={cn(pulseBar, "mt-1 h-3 w-full")} />
					<div className={cn(pulseBar, "mt-1 h-3 w-[72%]")} />
				</div>
				<div className="mt-0 flex flex-col gap-1">
					<div className={cn(pulseBar, "h-3 w-28")} />
					<div className="flex items-start justify-between gap-3 text-left">
						<div className={cn(pulseBar, "h-4 min-w-0 flex-1")} />
						<div className={cn(pulseBar, "h-4 w-16 shrink-0")} />
					</div>
				</div>
				<div className={cn(pulseBar, "mt-2 h-10 w-full rounded-full sm:mt-3")} />
			</div>
		</div>
	);
}

/** Green summary card only — checkout sits outside (matches [TotalSection]). */
export function BasketTotalsAsideSkeleton({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"w-full space-y-4 overflow-hidden rounded-[18px] bg-[#f3f8f7] p-5 dark:bg-core-midnight",
				className,
			)}
			aria-busy
			aria-label="Loading basket summary"
		>
			<div className="space-y-3 border-b border-border-visible pb-3">
				<div className={cn(pulseBar, "h-4 w-28")} />
				<div className="flex justify-between gap-4">
					<div className={cn(pulseBar, "h-4 w-20")} />
					<div className={cn(pulseBar, "h-4 w-16")} />
				</div>
				<div className="flex justify-between gap-4">
					<div className={cn(pulseBar, "h-4 w-24")} />
					<div className={cn(pulseBar, "h-4 w-16")} />
				</div>
			</div>
			<div className="space-y-3 border-t border-border-visible pt-3">
				<div className={cn(pulseBar, "h-4 w-32")} />
				<div className={cn(pulseBar, "h-[3.25rem] w-full rounded-[26px]")} />
				<div className={cn(pulseBar, "h-[3.25rem] w-full rounded-[26px]")} />
			</div>
			<div className="flex justify-between gap-4 border-t border-border-visible pt-3">
				<div className={cn(pulseBar, "h-5 w-16")} />
				<div className={cn(pulseBar, "h-5 w-20")} />
			</div>
		</div>
	);
}

/** Grid of basket line-card skeletons. */
export function BasketServiceGridSkeleton({ count }: { count: number }) {
	const n = Math.min(Math.max(count, 1), 8);
	return (
		<div
			className={cn(basketItemsColumnClass, basketItemsGridClass)}
			aria-busy
			aria-label="Loading basket items"
		>
			{Array.from({ length: n }, (_, i) => (
				<div key={i} className="h-auto min-h-0 w-full min-w-0">
					<BasketLineCardSkeleton />
				</div>
			))}
		</div>
	);
}

/** Matches loaded basket layout: grid + totals column. */
export function BasketPageContentSkeleton({ gridItemCount }: { gridItemCount: number }) {
	return (
		<>
			<BasketServiceGridSkeleton count={gridItemCount} />
			<div className={basketTotalsColumnClass}>
				<BasketTotalsAsideSkeleton />
				<div className={cn(pulseBar, "h-14 w-full rounded-full")} aria-hidden />
			</div>
		</>
	);
}

/** Navbar basket dropdown while service documents load. */
export function BasketDropdownSkeleton() {
	return (
		<div
			className="dropdown-glassmorphism flex w-screen flex-col gap-0 rounded-2xl p-0 sm:w-96"
			style={{
				border: "1px solid var(--glass-border)",
				boxShadow: "var(--glass-shadow)",
				background: "rgba(255, 255, 255, 0.25)",
			}}
			aria-busy
			aria-label="Loading basket"
		>
			<div className="flex h-12 shrink-0 items-center justify-center gap-3 px-6 pt-4">
				<div className={cn(pulseBar, "size-7 rounded-md")} />
				<div className={cn(pulseBar, "h-5 w-16")} />
			</div>
			<div className="flex flex-col gap-3 px-6 py-3">
				{[0, 1, 2].map((i) => (
					<div key={i} className="flex flex-col gap-2 border-y border-secondary/[0.06] py-2">
						<div className={cn(pulseBar, "h-4 w-full max-w-[14rem]")} />
						<div className={cn(pulseBar, "h-3 w-full max-w-[10rem]")} />
					</div>
				))}
			</div>
			<div className="flex w-full shrink-0 justify-center border-t border-secondary/[0.06] px-8 py-4">
				<div className={cn(pulseBar, "h-10 w-36 rounded-full")} />
			</div>
		</div>
	);
}
